import { SiteInfo } from "system/types/wireTypes";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { configActions } from "system/store/slices/config-slice";
import IRootState from "system/types/store/IRootState";
import { mainActions } from "system/store/slices/main-slice";
import AskAidenAccess from "system/types/enums/AskAidenAccess";

let lastTenantId = 0;

const useConfiguration = () => {
  const dispatch = useDispatch();
  const { tenantId } = useParams();
  const { tenants, userInfo, currentTenantId, defaultTenant, hasAdminPermission } = useSelector((state: IRootState) => state.config);
  const currentTenant = useMemo<SiteInfo | null>(() => {
    if (!tenants?.length) {
      return null;
    }

    let neededTenantId: number = 0;
    if (!tenantId || +tenantId < 1) {
      if (!lastTenantId) {
        return null;
      }
      neededTenantId = lastTenantId;
    } else {
      neededTenantId = +tenantId;
    }

    const tenant = tenants.find((x: SiteInfo) => x.siteId === +neededTenantId);
    if (!tenant) {
      lastTenantId = 0;
      return null;
    }

    lastTenantId = tenant.siteId;
    return tenant;
  }, [tenantId, tenants]);

  const roles = useMemo<string[]>(() => userInfo?.roles ?? [], [userInfo?.roles]);

  const isGlobalAdmin = useMemo<boolean>(() => {
    const hasPermissionC = userInfo?.permissions?.includes("C") ?? false;
    const hasPermissionG = userInfo?.permissions?.includes("G") ?? false;
    return hasPermissionC && hasPermissionG;
  }, [userInfo]);

  useEffect(() => {
    if (!currentTenant) {
      return;
    }

    // currentTenant is memoised per hook instance, so its reference changes on
    // every navigation and in each of the components using this hook. Comparing
    // the id instead means the filters are only dropped on a real tenant change,
    // rather than refetched on each hop.
    const siteId = currentTenant.siteId ?? 0;
    if (siteId === currentTenantId) {
      return;
    }

    dispatch(configActions.setCurrentTenantId(siteId));
    dispatch(mainActions.setFilters(null));
  }, [currentTenant, currentTenantId, dispatch]);

  useEffect(() => {
    if (!currentTenant || !userInfo) {
      return;
    }
    const hasPermissionU = currentTenant?.isAdmin ?? false;
    const hasPermissionC = userInfo?.permissions?.includes("C") ?? false;
    const hasPermissionG = userInfo?.permissions?.includes("G") ?? false;

    const hasPermission = hasPermissionC || hasPermissionU || hasPermissionG;
    dispatch(configActions.setHasAdminPermissions(hasPermission));
  }, [currentTenant, dispatch, userInfo]);

  const isFileModuleEnabled = useMemo<boolean>(() => {
    if (!tenants || !defaultTenant) {
      return false;
    }

    const tenant = tenants.find((x: SiteInfo) => x.siteId === +defaultTenant);
    if (!tenant) {
      return false;
    }

    return tenant.isFileModuleEnabled;
  }, [tenants, defaultTenant]);

  const isVulnerabilitiesModuleEnabled = useMemo<boolean>(() => {
    if (!tenants || !defaultTenant) {
      return false;
    }

    const tenant = tenants.find((x: SiteInfo) => x.siteId === +defaultTenant);
    if (!tenant) {
      return false;
    }

    return tenant.isVulnerabilitiesModuleEnabled;
  }, [tenants, defaultTenant]);

  const isPolicyEditorEnabled = useMemo<boolean>(() => {
    if (!tenants || !defaultTenant) {
      return false;
    }

    const tenant = tenants.find((x: SiteInfo) => x.siteId === +defaultTenant);
    if (!tenant) {
      return false;
    }

    return tenant.isPolicyEditorEnabled;
  }, [tenants, defaultTenant]);

  const isIntuneEnabled = useMemo<boolean>(() => {
    if (!tenants || !defaultTenant) {
      return false;
    }

    const tenant = tenants.find((x: SiteInfo) => x.siteId === +defaultTenant);
    if (!tenant) {
      return false;
    }

    return tenant.isIntuneEnabled;
  }, [tenants, defaultTenant]);


  const isAskAidenEnabled = useMemo<AskAidenAccess>(() => {
    if (!tenants || !defaultTenant) {
      return AskAidenAccess.NoAccess;
    }

    const tenant = tenants.find((x: SiteInfo) => x.siteId === +defaultTenant);
    if (!tenant) {
      return AskAidenAccess.NoAccess;
    }

    return tenant.askAidenAccess === 1
      ? AskAidenAccess.PromptsAccess
      : tenant.askAidenAccess === 2
      ? AskAidenAccess.FullAccess
      : AskAidenAccess.NoAccess;
  }, [tenants, defaultTenant]);
  const isAuditReportEnabled = useMemo<boolean>(() => {
    if (!tenants || !defaultTenant) {
      return false;
    }

    const tenant = tenants.find((x: SiteInfo) => x.siteId === +defaultTenant);
    if (!tenant) {
      return false;
    }

    return tenant.isAuditReportEnabled;
  }, [tenants, defaultTenant]);

  const isWidgetReportEnabled = useMemo<boolean>(() => {
    if (!tenants || !defaultTenant) {
      return false;
    }

    const tenant = tenants.find((x: SiteInfo) => x.siteId === +defaultTenant);
    if (!tenant) {
      return false;
    }

    return tenant.isWidgetReportEnabled;
  }, [tenants, defaultTenant]);

  const hasPermissionU = useMemo<boolean>(() => currentTenant?.isAdmin ?? false, [currentTenant?.isAdmin]);
  const hasPermissionC = useMemo<boolean>(() => userInfo?.permissions?.includes("C") ?? false, [userInfo?.permissions]);
  const hasPermissionG = useMemo<boolean>(() => userInfo?.permissions?.includes("G") ?? false, [userInfo?.permissions]);
  const hasPermissionR = useMemo<boolean>(() => userInfo?.permissions?.includes("R") ?? false, [userInfo?.permissions]);

  return {
    currentTenant,
    tenants,
    userInfo,
    isGlobalAdmin,
    currentTenantId,
    isFileModuleEnabled,
    isVulnerabilitiesModuleEnabled,
    isPolicyEditorEnabled,
    isIntuneEnabled,
    isAskAidenEnabled,
    isAuditReportEnabled,
    isWidgetReportEnabled,
    hasPermissionU,
    hasPermissionC,
    hasPermissionG,
    hasPermissionR,
    hasAdminPermission,
    roles,
  };
};

export default useConfiguration;
